import React, { useEffect, useMemo } from 'react';
import { getSeverityLabelFromScore, SeverityLabels } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Skeleton, Typography } from '@mui/material';
import { NoDataFoundWidget } from '../../../components/Widgets/layout.components';
import { AsyncQueryStatus } from '../../../hooks/queryObjectAsync/useQueryObjectAsyncWithCachedResults.utils';
import DraggableWidget from '../../CustomDashboards/components/DraggableWidget';
import BarWidget from '../../CustomDashboards/components/Widgets/BarWidget/BarWidget';
import LineWidget from '../../CustomDashboards/components/Widgets/LineWidget';
import PieWidget from '../../CustomDashboards/components/Widgets/PieWidget';
import renderWidget from '../../CustomDashboards/components/Widgets/RenderWidget';
import { useGetWidgetData } from '../../CustomDashboards/hooks/useGetWidgetData';
import { AllTypeWidgets, DwQueryRequest, WidgetCategory } from '../../CustomDashboards/types';

const SEVERITY_ORDER: string[] = [SeverityLabels.Critical, SeverityLabels.High, SeverityLabels.Medium, SeverityLabels.Low];
const MAX_RISK_SCORE_ROWS = 10000;

const getSeverityRank = (severity: string | null) => {
  const index = severity ? SEVERITY_ORDER.indexOf(severity) : -1;
  return index === -1 ? SEVERITY_ORDER.length : index;
};

const compareValues = (a: unknown, b: unknown) => {
  if (a === b) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return (a as any) < (b as any) ? -1 : 1;
};

export const getRiskScoreSeverityRequest = (
  request: DwQueryRequest,
  riskScoreField: string,
  severityField: string,
  hasStoredSeverity = true
): DwQueryRequest => ({
  ...request,
  select: {
    ...request.select,
    dims: [...request.select.dims.filter(({ name }) => hasStoredSeverity || name !== severityField), { name: riskScoreField }],
  },
  groupBy: [...(request.groupBy || []).filter(name => hasStoredSeverity || name !== severityField), riskScoreField],
  sorting: (request.sorting || []).filter(({ name }) => name !== severityField),
  top: { size: MAX_RISK_SCORE_ROWS, groupOthers: false, offset: 0 },
});

export const bucketByRiskScoreSeverity = ({
  rows = [],
  riskScoreField,
  severityField,
  metric,
  groupDims = [],
  hasStoredSeverity = true,
}: {
  rows?: Record<string, any>[];
  riskScoreField: string;
  severityField: string;
  metric: string;
  groupDims?: string[];
  hasStoredSeverity?: boolean;
}): Record<string, any>[] => {
  const buckets = new Map<string, Record<string, any>>();
  rows.forEach(row => {
    const count = Number(row[metric]) || 0;
    if (!count) return;
    const riskScore = row[riskScoreField];
    if (!hasStoredSeverity && riskScore == null) return;
    const severity = (riskScore != null ? getSeverityLabelFromScore(Number(riskScore)) : row[severityField]) || null;
    const key = JSON.stringify([...groupDims.map(dim => row[dim]), severity]);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket[metric] += count;
    } else {
      buckets.set(key, { ...Object.fromEntries(groupDims.map(dim => [dim, row[dim]])), [severityField]: severity, [metric]: count });
    }
  });
  return [...buckets.values()].sort((a, b) => {
    const groupDiff = groupDims.reduce((diff, dim) => diff || compareValues(a[dim], b[dim]), 0);
    return groupDiff || getSeverityRank(a[severityField]) - getSeverityRank(b[severityField]);
  });
};

interface RiskScoreSeverityWidgetProps {
  widget: AllTypeWidgets;
  riskScoreField: string;
  severityField: string;
  hasStoredSeverity?: boolean;
}

const RiskScoreSeverityWidget: React.FC<RiskScoreSeverityWidgetProps> = ({
  widget,
  riskScoreField,
  severityField,
  hasStoredSeverity = true,
}) => {
  const [request] = widget.requests;
  const metric = request.select.metrics[0].alias || request.select.metrics[0].name;
  const groupDims = request.select.dims.map(({ alias, name }) => alias || name).filter(dim => dim !== severityField);

  const riskScoreWidget = useMemo(
    () =>
      ({ ...widget, requests: [getRiskScoreSeverityRequest(request, riskScoreField, severityField, hasStoredSeverity)] }) as AllTypeWidgets,
    [widget, riskScoreField, severityField, hasStoredSeverity]
  );
  const {
    data,
    errors,
    isLoading: isLoadingData,
    isAsyncQuery,
    isDebouncing,
    asyncQueryStatus,
  } = useGetWidgetData({ widget: riskScoreWidget });
  const isLoading =
    isLoadingData ||
    (isAsyncQuery && (isDebouncing || asyncQueryStatus === AsyncQueryStatus.Loading)) ||
    asyncQueryStatus === AsyncQueryStatus.Polling;
  const bucketedData = useMemo(
    () => bucketByRiskScoreSeverity({ rows: data, riskScoreField, severityField, metric, groupDims, hasStoredSeverity }),
    [data, riskScoreField, severityField, metric, groupDims.join(), hasStoredSeverity]
  );

  const shouldFallback = hasStoredSeverity && !isLoading && (!!errors?.length || !bucketedData.length);

  useEffect(() => {
    if (shouldFallback) {
      console.warn(
        `Risk score severity query returned no data for "${widget.definition.title.title}", falling back to ${severityField}`,
        errors
      );
    }
  }, [shouldFallback]);

  if (shouldFallback) {
    return (
      <DraggableWidget
        widget={widget}
        customToolbar={
          <Typography sx={{ whiteSpace: 'nowrap', marginBottom: '8px' }} variant="h6">
            {widget.definition.title.title}
          </Typography>
        }>
        {renderWidget({ widget })}
      </DraggableWidget>
    );
  }

  const renderChart = () => {
    if (isLoading) {
      return <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 1 }} />;
    }
    if (!bucketedData.length) {
      return <NoDataFoundWidget showImage />;
    }
    switch (widget.category) {
      case WidgetCategory.Bar:
        return <BarWidget widget={widget} data={bucketedData} />;
      case WidgetCategory.Pie:
        return (
          <PieWidget
            widget={widget}
            data={bucketedData}
            responseTotals={{ [metric]: bucketedData.reduce((sum, row) => sum + row[metric], 0) }}
          />
        );
      case WidgetCategory.Line:
        return <LineWidget widget={widget} data={bucketedData} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ pl: 3, pr: 2, pt: 2, height: '100%', ...flex.col }}>
      <Typography sx={{ whiteSpace: 'nowrap', marginBottom: '8px' }} variant="h6">
        {widget.definition.title.title}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>{renderChart()}</Box>
    </Box>
  );
};

export default RiskScoreSeverityWidget;
